import {FormControl, FormGroup} from '@angular/forms';

import {forkJoin} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import * as moment from 'moment';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    DocumentTypesEnum,
    IndividualDocumentsService,
    IndividualIdentityDetailsService,
    IndividualDocumentModel,
    IndividualExtendedModel,
    IndividualIdentityDetailModel,
    IndividualDocumentStatusTriggerModel,
    CompanyExtendedModel,
    CompanyDocumentModel,
    CompanyDocumentsService,
    CompanyDocumentStatusTriggerModel
} from '@spa/api';

import {MinDateValidator, RequiredValidatorKey} from '@spa/validators';

import {BaseControl, SelectControl} from '../../../shared/classes/controls';
import {CheckboxControl, DatepickerControl, Helper} from '../../../shared/classes';

import {
    DocumentsEditControls,
    DocumentsEditNumberSelector,
    DocumentsEditStatusSelector,
    DocumentsEditSubTypeSelector,
    DocumentsEditExpiresAtSelector,
    DocumentsEditDescriptionSelector,
    DocumentsEditVisibleForClientSelector,
} from '../../../../assets/descriptors';

export class DocumentDetailsForm {
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;

    private document: IndividualDocumentModel | CompanyDocumentModel;
    private documentStatusTriggers: number[];

    public formGroup: FormGroup;
    public controls: BaseControl[];

    constructor(
        private clientModel: IndividualExtendedModel | CompanyExtendedModel,
        private currentDocument: IndividualDocumentModel | CompanyDocumentModel,
        private identityDetail: IndividualIdentityDetailModel,
        private individualDocumentsService: IndividualDocumentsService,
        private individualIdentityDetailsService: IndividualIdentityDetailsService,
        private isCorporateClient: boolean,
        private companyDocumentsService: CompanyDocumentsService,
    ) {
        this.document = currentDocument;
        this.client = clientModel;
    }

    private initDocumentDetails(control: BaseControl, documentType: string): void {
        const trigger = Helper.enumsHelper().getDocumentStatusTriggerByStatus(this.document.status);

        switch (control.name) {
            case DocumentsEditSubTypeSelector:
                let lookup = null;

                switch (documentType) {
                    case DocumentTypesEnum.ProofOfAddress:
                        lookup = Helper.enumsHelper().getProofOfAddressDocumentTypes();
                        break;
                    case DocumentTypesEnum.ProofOfIdentity:
                        lookup = Helper.enumsHelper().getProofOfIdentityDocumentTypes();
                        break;
                    case DocumentTypesEnum.MarketDataAgreement:
                    case DocumentTypesEnum.Selfie:
                        control.disabled = true;
                        break;
                }

                if (lookup) {
                    (control as SelectControl).options = lookup.getTypeKeys().map((key: string) => {
                        return {
                            label: lookup.getTypeValue(key),
                            value: key
                        };
                    });
                }

                control.value = this.document.subDocumentType;
                break;
            case DocumentsEditStatusSelector:
                const triggers = (this.documentStatusTriggers.indexOf(trigger) < 0) ? this.documentStatusTriggers.concat(trigger) :
                    this.documentStatusTriggers;

                (control as SelectControl).options = triggers.map((statusTrigger: number) => {
                    return {
                        label: Helper.textsHelper().getDocumentStatusNameByTrigger(statusTrigger),
                        value: statusTrigger
                    };
                });

                control.value = trigger;
                break;
            case DocumentsEditVisibleForClientSelector:
                (control as CheckboxControl).value = !this.document.isHiddenForClient;
                (control as CheckboxControl).setChecked(!this.document.isHiddenForClient, true);
                break;
            case DocumentsEditDescriptionSelector:
                control.value = this.document.reviewSummary;
                break;
        }
    }

    private getUpdateStatusRequest(trigger: number): ServerResponse<null> {
        if (!this.isCorporateClient) {
            const individualStatusTriggerModel = new IndividualDocumentStatusTriggerModel(
                this.document.id,
                trigger
            );

            return this.individualDocumentsService.changeStatus(individualStatusTriggerModel).pipe(
                switchMap(this.updateDocumentStatus.bind(this, trigger))
            );
        }

        const model = new CompanyDocumentStatusTriggerModel(
            this.document.id,
            trigger
        );

        return this.companyDocumentsService.changeStatus(model).pipe(
            switchMap(this.updateDocumentStatus.bind(this, trigger))
        );
    }

    private updateDocumentStatus(
        trigger: number,
        response: ServerResponseData<null>
    ): ServerResponse<boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable<null>(response);
        }

        this.document.status = Helper.enumsHelper().getDocumentStatusByTrigger(trigger);

        const request = !this.isCorporateClient ? this.individualDocumentsService.getStatusTriggers(this.document.id) :
            this.companyDocumentsService.getStatusTriggers(this.document.id);

        return request.pipe(
            map((statusTriggersResponse: ServerResponseData<number[]>) => {
                if (statusTriggersResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<number[]>(response);
                }

                this.documentStatusTriggers = statusTriggersResponse.data as number[];

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private checkDocumentForChanges(): boolean {
        return (
            (this.document.subDocumentType !== this.formGroup.value[DocumentsEditSubTypeSelector]) ||
            (this.document.reviewSummary !== this.formGroup.value[DocumentsEditDescriptionSelector]) ||
            (this.document.isHiddenForClient !== !this.formGroup.value[DocumentsEditVisibleForClientSelector])
        );
    }

    private checkIdentityDetailsForChanges(): boolean {
        return (
            (this.identityDetail.identityNumber !== this.formGroup.value[DocumentsEditNumberSelector]) ||
            (this.identityDetail.expirationDate !== this.formGroup.value[DocumentsEditExpiresAtSelector]) ||
            (this.identityDetail.identityDocumentType !== this.formGroup.value[DocumentsEditSubTypeSelector])
        );
    }

    private createDocumentChangeRequest(): ServerResponse<boolean> {
        const clonedDocument = this.document;
        const documentModel = this.document;

        if (!this.isCorporateClient) {
            (documentModel as IndividualDocumentModel).subDocumentType = this.formGroup.value[DocumentsEditSubTypeSelector];
            (documentModel as IndividualDocumentModel).reviewSummary = this.formGroup.value[DocumentsEditDescriptionSelector];
            (documentModel as IndividualDocumentModel).isHiddenForClient = !this.formGroup.value[DocumentsEditVisibleForClientSelector];
        } else {
            (documentModel as CompanyDocumentModel).subDocumentType = this.formGroup.value[DocumentsEditSubTypeSelector];
            (documentModel as CompanyDocumentModel).reviewSummary = this.formGroup.value[DocumentsEditDescriptionSelector];
            (documentModel as CompanyDocumentModel).isHiddenForClient = !this.formGroup.value[DocumentsEditVisibleForClientSelector];
        }

        const request = !this.isCorporateClient ? this.individualDocumentsService.update(documentModel as IndividualDocumentModel) :
            this.companyDocumentsService.update(documentModel as CompanyDocumentModel);

        return request.pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.document = clonedDocument;

                    return Helper.errorsHelper().createErrorResponse<null>(response);
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private createIdentityDocumentChangeRequest(): ServerResponse<boolean> {
        const clonedIdentityDetail = this.identityDetail;
        const identityModel = this.identityDetail;
        const individual = this.client as IndividualExtendedModel;

        identityModel.identityNumber = this.formGroup.value[DocumentsEditNumberSelector];
        identityModel.expirationDate = this.formGroup.value[DocumentsEditExpiresAtSelector];
        identityModel.identityDocumentType = this.formGroup.value[DocumentsEditSubTypeSelector];

        return this.individualIdentityDetailsService.update(identityModel).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    individual.identityDetails = individual.identityDetails.map((detail: IndividualIdentityDetailModel) => {
                        if (detail.id === clonedIdentityDetail.id) {
                            detail = clonedIdentityDetail;
                        }

                        return detail;
                    });

                    return Helper.errorsHelper().createErrorResponse<null>(response);
                }


                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(statusTriggers: number[]): void {
        this.documentStatusTriggers = statusTriggers;

        this.formGroup = new FormGroup({});

        this.controls = DocumentsEditControls().map((control: BaseControl) => {
            if (this.document) {
                this.initDocumentDetails(control, this.document.documentType);

                if (this.document.documentType === DocumentTypesEnum.ProofOfIdentity) {
                    switch (control.name) {
                        case DocumentsEditNumberSelector:
                            control.value = this.identityDetail.identityNumber;
                            break;
                        case DocumentsEditExpiresAtSelector:
                            control.value = this.identityDetail.expirationDate;
                            control.disabled = !this.identityDetail.expirationDate;

                            const minDate = moment().format('YYYY-MM-DD');

                            (control as DatepickerControl).minDate = minDate;

                            control.extendValidators(MinDateValidator(minDate));
                            break;
                    }
                }
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        const documentDetailsControls = this.formGroup.controls;

        const statuses = Object.keys(documentDetailsControls).map((key: string) => {
            this.controls.map((formsControl: BaseControl) => {
                if (documentDetailsControls[key].invalid) {
                    if (formsControl.name === key) {
                        formsControl.errors = Object
                            .keys(documentDetailsControls[key].errors)
                            .map((errorKey: string) => {
                                return messageFunction(formsControl.placeholder)[errorKey];
                            });
                    }

                    return false;
                }

                if ((formsControl.name === DocumentsEditSubTypeSelector) && !formsControl.value && !formsControl.disabled) {
                    formsControl.setError(messageFunction(formsControl.placeholder)[RequiredValidatorKey]);

                    return false;
                }
            });

            return true;
        });

        return statuses.indexOf(false) < 0;
    }

    createRequest(): ServerResponse<number[]> | null {
        const trigger = this.formGroup.value[DocumentsEditStatusSelector];
        const requestsStack = [];

        if (Helper.enumsHelper().getDocumentStatusTriggerByStatus(this.document.status) !== trigger) {
            requestsStack.push(this.getUpdateStatusRequest(trigger));
        }

        if (this.checkDocumentForChanges()) {
            requestsStack.push(this.createDocumentChangeRequest());
        }

        if (
            (this.document.documentType === DocumentTypesEnum.ProofOfIdentity) &&
            this.checkIdentityDetailsForChanges()
        ) {
            requestsStack.push(this.createIdentityDocumentChangeRequest());
        }

        if (requestsStack.length > 0) {
            return forkJoin(requestsStack).pipe(
                map((responses: ServerResponseData<boolean>[]) => {
                    if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                        return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                    }

                    return Helper.createSuccessResponse<number[]>(this.documentStatusTriggers);
                })
            );
        }

        return null;
    }
}
